import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import Mashy from "@/components/Mashy";
import ProgressBar from "@/components/ProgressBar";
import { Flame, Star } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function Progress() {
  const { profile } = useAuth();
  const [summary, setSummary] = useState(null);
  const [attempts, setAttempts] = useState([]);

  useEffect(() => {
    api.get("/progress/summary").then((r) => setSummary(r.data));
    api.get("/attempts/me").then((r) => setAttempts(r.data.attempts || []));
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F9FC] pb-24" data-testid="progress-screen">
      <header className="px-5 pt-6 max-w-xl mx-auto">
        <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Your Journey</div>
        <h1 className="font-display text-2xl">Progress & Mastery</h1>
      </header>

      <main className="max-w-xl mx-auto px-5 mt-5 flex flex-col gap-5">
        <div className="card-chunky p-5 flex items-center gap-4">
          <Mashy mood="celebrate" size={80} />
          <div className="flex-1">
            <div className="flex gap-3">
              <div className="flex items-center gap-1 text-[#FF9600] font-bold"><Flame className="w-4 h-4" /> {profile?.current_streak || 0} day streak</div>
              <div className="flex items-center gap-1 text-[#FFC800] font-bold"><Star className="w-4 h-4" /> {profile?.total_xp || 0} XP</div>
            </div>
            <div className="text-sm text-gray-500 mt-1">Every small win counts. Mashy is proud!</div>
          </div>
        </div>

        <div className="card-chunky p-5">
          <div className="font-display text-lg mb-3">Mastery by Subject</div>
          {summary && Object.keys(summary.by_subject || {}).length > 0 ? (
            Object.entries(summary.by_subject).map(([s, b]) => (
              <div key={s} className="mb-3" data-testid={`progress-subject-${s}`}>
                <div className="flex justify-between text-sm font-bold mb-1">
                  <span className="capitalize">{s}</span>
                  <span>{Math.round((b.mastery || 0) * 100)}%</span>
                </div>
                <ProgressBar value={(b.mastery || 0) * 100} color={s === "math" ? "#1CB0F6" : "#58CC02"} testId={`progress-bar-${s}`} />
              </div>
            ))
          ) : (
            <div className="text-sm text-gray-500">Start a lesson to see mastery here.</div>
          )}
        </div>

        <div className="card-chunky p-5">
          <div className="font-display text-lg mb-3">Topics to Practice</div>
          {summary?.weak_topics?.length ? summary.weak_topics.map((w, i) => (
            <div key={i} className="flex justify-between border-b border-gray-100 py-2 text-sm">
              <span>{w.topic} <span className="text-gray-400">({w.subject})</span></span>
              <span className="font-bold text-[#FF9600]">{Math.round((w.mastery_score || 0) * 100)}%</span>
            </div>
          )) : <div className="text-sm text-gray-500">No weak topics yet — keep going!</div>}
        </div>

        <div className="card-chunky p-5">
          <div className="font-display text-lg mb-3">Recent Attempts</div>
          {attempts.length ? attempts.slice(0, 6).map((a) => (
            <div key={a.attempt_id} className="flex justify-between border-b border-gray-100 py-2 text-sm">
              <span className="truncate flex-1">{a.lesson_id}</span>
              <span className={`font-bold ${a.score >= 0.5 ? "text-[#58CC02]" : "text-[#FF9600]"}`}>+{a.xp_earned} XP</span>
            </div>
          )) : <div className="text-sm text-gray-500">No attempts yet.</div>}
        </div>
      </main>
    </div>
  );
}
